import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { adminService, staffService } from '../../services';
import { useUser } from '../../context/UserContext';
import { AdminModal, AdminPageHeader, DataTable, MoreMenu, SearchField, Btn, SubTabs, Field, FieldLabel, inputClass, money, Loading, useAdminShell } from '../../components/admin/shell';
import InviteCardSheet from '../../components/admin/InviteCardSheet';
import WalletMoveModal from '../../components/admin/WalletMoveModal';
import UserRecord from '../../components/admin/UserRecord';

const feeWord = (cents) => (cents == null ? 'DEFAULT' : cents === 0 ? 'WAIVED' : money(cents));

/**
 * PEOPLE › USERS — spec/sections.md §4. The role select is gone: access changes only in
 * STAFF. Verify and the PR-fee override are `users` · edit, DELETE is `users` · delete,
 * ADJUST WALLET is `wallets` · adjust, TRANSFER WALLET is superadmin. Every row action sits
 * behind the row's ⋯ (Oct 28 2026). The detail panel is a nested route.
 */
export default function UsersPage() {
  const { can, isSuperadmin } = useUser();
  const navigate = useNavigate();
  const { openPerson, canSeePeople } = useAdminShell();
  const { id: selectedId } = useParams();
  const [params, setParams] = useSearchParams();
  const canEdit = can('users', 'edit');
  const canDelete = can('users', 'delete');
  const canAdjust = can('wallets', 'adjust');
  const [search, setSearch] = useState(params.get('search') || '');
  const [page, setPage] = useState(1);
  const tab = params.get('tab') || 'all';
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [feeEdit, setFeeEdit] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // INVITE CARD: `{ kind: 'user', id, name }` while the sheet is open.
  const [inviteTarget, setInviteTarget] = useState(null);
  // WALLET: `{ person, mode }` while the wallet modal is open.
  const [walletFor, setWalletFor] = useState(null);
  const walletPerson = (u) => ({ id: u.id, name: u.displayName || u.email, email: u.email, balance: u.walletBalance || 0, escrow: u.walletEscrow || 0 });

  const applied = params.get('search') || '';
  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminService.getUsers(page, applied, tab);
    setLoading(false);
    if (r.success) { setRows(r.data.users || []); setTotal(r.data.total || 0); }
  }, [page, applied, tab]);
  useEffect(() => { load(); }, [load]);

  const submitSearch = () => { const next = new URLSearchParams(params); if (search) next.set('search', search); else next.delete('search'); setParams(next, { replace: true }); setPage(1); };
  // The tab is a server-side filter, not a client slice: the count under the table has to
  // mean the same thing as the rows above it.
  const setTab = (key) => { const next = new URLSearchParams(params); if (key === 'all') next.delete('tab'); else next.set('tab', key); setParams(next, { replace: true }); setPage(1); };

  const verify = async (u) => { const r = await adminService.verifyUser(u.id); if (r.success) load(); };
  const flagDemo = async (u) => { const r = await adminService.setTestAccount(u.id, !u.isTestAccount); if (r.success) load(); };

  const noun = { all: 'accounts', hosts: 'people who have made an event', guests: 'people who have asked for a seat', staff: 'people with a key', unverified: 'accounts that have not been verified' }[tab] || 'accounts';
  const meta = canDelete ? `${total.toLocaleString()} ${noun}. Search by email or name.`
    : canEdit ? `${total.toLocaleString()} ${noun}. You can verify and override fees; deleting and wallets are not yours.`
      : `${total.toLocaleString()} ${noun}.`;

  return (
    <>
      <AdminPageHeader section="users" meta={meta} />
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <SubTabs
            tabs={[
              { key: 'all', label: 'ALL' }, { key: 'hosts', label: 'HOSTS' }, { key: 'guests', label: 'GUESTS' },
              { key: 'staff', label: 'STAFF' }, { key: 'unverified', label: 'UNVERIFIED' },
            ]}
            active={tab}
            onChange={setTab}
          />
          <SearchField value={search} onChange={setSearch} onSubmit={submitSearch} placeholder="SEARCH EMAIL OR NAME" />
          <Btn variant="solid" size="card" onClick={submitSearch}>SEARCH</Btn>
        </div>
        <DataTable
          columns={[
            { key: 'email', label: 'EMAIL', fr: 2.2 },
            { key: 'displayName', label: 'NAME', fr: 1.3, render: (u) => <>{u.displayName || '—'}{u.isTestAccount ? <span className="ml-2 text-[10px] font-bold">{u.isCrawler ? 'PLAY CRAWLER' : 'DEMO'}</span> : null}</> },
            { key: 'role', label: 'ROLE', fr: 0.8, render: (u) => (u.role || 'user').toUpperCase() },
            { key: 'identityVerified', label: 'VERIFIED', fr: 0.8, render: (u) => (u.identityVerified ? 'YES' : 'NO') },
            { key: 'walletBalance', label: 'BALANCE', fr: 0.9, right: true, render: (u) => money(u.walletBalance) },
            { key: 'walletEscrow', label: 'ESCROW', fr: 0.9, right: true, render: (u) => money(u.walletEscrow) },
            { key: 'prServiceFeeOverrideCents', label: 'PORTAL FEE', fr: 0.8, right: true, render: (u) => feeWord(u.prServiceFeeOverrideCents) },
          ]}
          rows={rows}
          loading={loading}
          onRowClick={canSeePeople ? (u) => openPerson(u.id) : undefined}
          hasActions={canEdit || canDelete || canAdjust || isSuperadmin}
          actionsMenu
          actions={(u) => [
            { label: 'VIEW', icon: 'view', onClick: () => navigate(`/admin/users/${u.id}`) },
            canEdit && !u.identityVerified && { label: 'OVERRIDE', icon: 'verify', onClick: () => verify(u) },
            canEdit && { label: 'PORTAL FEE', icon: 'fee', onClick: () => setFeeEdit(u) },
            canEdit && { label: 'INVITE CARD', icon: 'card', onClick: () => setInviteTarget({ kind: 'user', id: u.id, name: u.displayName || u.email }) },
            isSuperadmin && { label: u.isTestAccount ? 'DEMO ✓' : 'FLAG DEMO', icon: 'flag', onClick: () => flagDemo(u) },
            { divider: true },
            canAdjust && { label: 'ADJUST WALLET', icon: 'wallet', onClick: () => setWalletFor({ person: walletPerson(u), mode: 'adjust' }) },
            isSuperadmin && { label: 'TRANSFER WALLET', icon: 'transfer', variant: 'magenta', onClick: () => setWalletFor({ person: walletPerson(u), mode: 'transfer' }) },
            { divider: true },
            canDelete && u.role !== 'superadmin' && { label: 'DELETE', icon: 'trash', variant: 'danger', onClick: () => setDeleteTarget(u) },
          ].filter(Boolean)}
          total={total}
          page={page}
          pageSize={20}
          onPage={setPage}
        />
        {selectedId && (
          <UserDetailPanel
            userId={selectedId}
            onBack={() => navigate('/admin/users')}
            onDelete={(u) => setDeleteTarget(u)}
            onChanged={load}
            onInviteCard={(u) => setInviteTarget({ kind: 'user', id: u.id, name: u.displayName || u.email })}
          />
        )}
      </div>

      {/* Page-level modals: opened from the table AND the detail panel. */}
      <PrFeeModal user={feeEdit} onClose={() => setFeeEdit(null)} onSaved={() => { setFeeEdit(null); load(); }} />
      <InviteCardSheet target={inviteTarget} onClose={() => setInviteTarget(null)} />
      <WalletMoveModal person={walletFor?.person} initialMode={walletFor?.mode} onClose={() => setWalletFor(null)} onDone={() => load()} />
      <DeleteUserModal user={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={() => { setDeleteTarget(null); if (selectedId === deleteTarget?.id) navigate('/admin/users'); load(); }} />
    </>
  );
}

/** PORTAL FEE — the same modal the drawer opens, so one control looks one way. */
function PrFeeModal({ user, onClose, onSaved }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (user) { setInput(user.prServiceFeeOverrideCents == null ? '' : (user.prServiceFeeOverrideCents / 100).toFixed(2)); setError(''); } }, [user]);
  const save = async () => {
    const trimmed = input.trim();
    let cents = null;
    if (trimmed !== '') {
      const dollars = parseFloat(trimmed);
      if (Number.isNaN(dollars) || dollars < 0) { setError('A number that is not negative, or empty to clear it.'); return; }
      cents = Math.round(dollars * 100);
    }
    setSaving(true);
    const r = await adminService.setPrFeeOverride(user.id, cents);
    setSaving(false);
    if (r.success) onSaved(); else setError(r.error || 'Could not save.');
  };
  if (!user) return null;
  return (
    <AdminModal
      open
      onClose={onClose}
      title="PORTAL SERVICE FEE"
      kicker={`What ${user.email}'s guests pay on every checkout. Empty is the $1 default; 0 waives it entirely.`}
      footer={<><Btn size="sheet" onClick={onClose}>CANCEL</Btn><Btn size="sheet" variant="solid" onClick={save} disabled={saving}>{saving ? 'SAVING…' : 'SAVE FEE'}</Btn></>}
    >
      <Field label="FEE IN DOLLARS" error={error}>
        <input type="number" min="0" step="0.01" value={input} onChange={(e) => setInput(e.target.value)} placeholder="1.00" className={inputClass} aria-label="Fee in dollars" autoFocus />
      </Field>
      <FieldLabel className="pt-3">STRIPE'S PROCESSING FEE (2.9% + 30¢) IS A PASS-THROUGH AND IS NEVER AFFECTED BY THIS.</FieldLabel>
    </AdminModal>
  );
}

/** DELETE USER — irreversible, so it says exactly what goes. */
function DeleteUserModal({ user, onClose, onDeleted }) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setError(''); }, [user]);
  const del = async () => {
    setBusy(true);
    const r = await adminService.deleteUser(user.id);
    setBusy(false);
    if (r.success) onDeleted(); else setError(r.error || 'Could not delete.');
  };
  if (!user) return null;
  return (
    <AdminModal
      open
      onClose={onClose}
      title="DELETE THIS ACCOUNT"
      kicker={`${user.email} and everything attached to it — profile, wallet, applications and their draft or ended events — goes for good. This cannot be undone.`}
      footer={<><Btn size="sheet" onClick={onClose}>CANCEL</Btn><Btn size="sheet" variant="red" onClick={del} disabled={busy}>{busy ? 'DELETING…' : 'DELETE USER'}</Btn></>}
    >
      {error ? <p className="m-0 font-mono text-[12px] text-[#DC2626]">{error}</p> : <FieldLabel>NOTHING HERE COMES BACK.</FieldLabel>}
    </AdminModal>
  );
}

function UserDetailPanel({ userId, onBack, onDelete, onChanged, onInviteCard }) {
  const { can, isSuperadmin } = useUser();
  const panelRef = useRef(null);
  useEffect(() => { panelRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, [userId]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffRow, setStaffRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminService.getUserDetail(userId);
    if (r.success) setData(r.data);
    setLoading(false);
  }, [userId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isSuperadmin) return;
    (async () => { const r = await staffService.person(userId); setStaffRow(r.success ? r.data : null); })();
  }, [userId, isSuperadmin]);

  if (loading) return <div className="rounded-[24px] bg-white p-6"><Loading /></div>;
  if (!data) return <div className="rounded-[24px] bg-white p-6 font-mono text-[11px] text-[#DC2626]">COULD NOT LOAD THIS USER.</div>;
  const u = data.user;

  return (
    <section ref={panelRef} className="overflow-hidden rounded-[24px] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-4 px-5 pb-4 pt-5">
        <div className="min-w-0">
          <div className="font-mono text-[13px] font-bold">{u.email}</div>
          <div className="font-mono text-[11px] text-[#6B7280]">
            {u.displayName || 'No display name'} · {(u.role || 'user').toLowerCase()} · Joined {new Date(u.createdAt).toLocaleDateString()}
            {u.identityVerified && <span className="ml-2 font-bold text-[#15803D]">VERIFIED</span>}
            {isSuperadmin && staffRow && <> · <Link to={`/admin/staff/${u.id}`} className="font-bold underline">OPEN IN STAFF →</Link></>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {(u.handle || u.id) && <a href={u.handle ? `/${u.handle}` : `/users/${u.id}`} target="_blank" rel="noopener noreferrer" className="flex h-8 items-center rounded-full bg-[#F2F2F2] px-[14px] font-mono text-[9px] font-bold uppercase tracking-[0.12em] hover:bg-black hover:text-white">OPEN PORTAL ↗</a>}
          {can('users', 'delete') && u.role !== 'superadmin' && <Btn variant="danger" onClick={() => onDelete(u)} className="px-[11px] py-[7px]">DELETE USER</Btn>}
          <Btn onClick={onBack} className="px-[11px] py-[7px]">BACK</Btn>
          {can('users', 'edit') && (
            <MoreMenu label="More actions" testId="user-more" items={[{ label: 'DOWNLOAD INVITE CARD', icon: 'card', onClick: () => onInviteCard?.(u), testId: 'user-more-invite-card' }]} />
          )}
        </div>
      </div>

      <UserRecord userId={userId} onChanged={onChanged} />
    </section>
  );
}
